import dayjs from "dayjs";
import { Filter } from "lucide-react";
import { cn } from "@/lib/utils";
import { RANGE_PRESETS, GROUPING_OPTIONS } from "../../lib/progress";

export const SHELL_HEADER_OFFSET_PX = 73;

export const SPORT_OPTIONS = [
  { value: "all", label: "All activities" },
  { value: "cycling", label: "Cycling" },
  { value: "running", label: "Running" },
  { value: "walking", label: "Walking" },
  { value: "hiking", label: "Hiking" },
  { value: "swimming", label: "Swimming" },
  { value: "other", label: "Other" },
];

export const buildActiveFilterSummary = ({
  sport,
  rangePreset,
  grouping,
  customStart,
  customEnd,
}) => {
  const sportLabel =
    SPORT_OPTIONS.find((option) => option.value === sport)?.label || sport;
  let rangeLabel =
    RANGE_PRESETS.find((preset) => preset.key === rangePreset)?.label || rangePreset;

  if (rangePreset === "custom") {
    if (customStart && customEnd) {
      rangeLabel = `${dayjs(customStart).format("D MMM YYYY")} – ${dayjs(customEnd).format("D MMM YYYY")}`;
    } else if (customStart || customEnd) {
      const partial = customStart
        ? dayjs(customStart).format("D MMM YYYY")
        : dayjs(customEnd).format("D MMM YYYY");
      rangeLabel = `${partial} – …`;
    } else {
      rangeLabel = "Custom range";
    }
  }

  const groupingLabel =
    GROUPING_OPTIONS.find((option) => option.key === grouping)?.label || grouping;

  return `${sportLabel} · ${rangeLabel} · ${groupingLabel}`;
};

const inputCls = "app-control text-xs tracking-wide font-mono";

const FilterField = ({ label, htmlFor, children }) => (
  <div className="block w-full min-w-0">
    <label htmlFor={htmlFor} className="card-label mb-1.5 block">
      {label}
    </label>
    {children}
  </div>
);

export const ProgressFilters = ({
  sport,
  onSportChange,
  rangePreset,
  onRangePresetChange,
  grouping,
  onGroupingChange,
  customStart,
  onCustomStartChange,
  customEnd,
  onCustomEndChange,
  activeSummary,
}) => (
  <section
    className={cn(
      "app-card px-4 py-4 sm:px-5 sm:py-5 mb-8 w-full max-w-full overflow-x-hidden",
      "md:sticky md:top-[73px] md:z-30 md:bg-[var(--surface-primary)]/95 md:backdrop-blur-sm md:shadow-app-sm"
    )}
    data-testid="progress-filters"
  >
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-4">
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-brand-primary shrink-0" strokeWidth={1.5} aria-hidden="true" />
        <span className="card-label">Filters</span>
      </div>
      {activeSummary ? (
        <p
          className="text-xs text-dark-text-secondary break-words sm:text-right"
          data-testid="progress-active-filter-summary"
        >
          {activeSummary}
        </p>
      ) : null}
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
      <FilterField label="Sport" htmlFor="progress-sport-filter">
        <select
          id="progress-sport-filter"
          value={sport}
          onChange={(e) => onSportChange(e.target.value)}
          className={inputCls}
          data-testid="progress-sport-filter"
        >
          {SPORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </FilterField>

      <FilterField label="Date range" htmlFor="progress-range-filter">
        <select
          id="progress-range-filter"
          value={rangePreset}
          onChange={(e) => onRangePresetChange(e.target.value)}
          className={inputCls}
          data-testid="progress-range-filter"
        >
          {RANGE_PRESETS.map((preset) => (
            <option key={preset.key} value={preset.key}>
              {preset.label}
            </option>
          ))}
        </select>
      </FilterField>

      <FilterField label="Grouping" htmlFor="progress-grouping-filter">
        <select
          id="progress-grouping-filter"
          value={grouping}
          onChange={(e) => onGroupingChange(e.target.value)}
          className={inputCls}
          data-testid="progress-grouping-filter"
        >
          {GROUPING_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </select>
      </FilterField>
    </div>

    {rangePreset === "custom" && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 w-full">
        <FilterField label="Custom start" htmlFor="progress-custom-start">
          <input
            id="progress-custom-start"
            type="date"
            value={customStart}
            onChange={(e) => onCustomStartChange(e.target.value)}
            className={inputCls}
            data-testid="progress-custom-start"
          />
        </FilterField>
        <FilterField label="Custom end" htmlFor="progress-custom-end">
          <input
            id="progress-custom-end"
            type="date"
            value={customEnd}
            onChange={(e) => onCustomEndChange(e.target.value)}
            className={inputCls}
            data-testid="progress-custom-end"
          />
        </FilterField>
      </div>
    )}
  </section>
);

export { inputCls };
